import {
  Button,
  Dropdown as CommonDropdown,
  DropdownButton,
  DropdownMenu,
  DropdownMenuItem,
  Icon,
  InfiniteTable,
  InfiniteTableColumns,
  InfiniteTableSortBy,
  Link as CommonLink,
  MultiSelect,
  Pill,
  Popover,
  Stack,
  Table as CommonUITable,
  TableColumns,
  Text,
  Box,
} from '@sixfold/common-ui';
import { BackofficeScope } from '@sixfold/session-interface';
import { isNil, notNil } from '@sixfold/typed-primitives';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, NavLink, useParams } from 'react-router';

import { DeleteRelationshipAction } from './delete_relationship_action';
import {
  getOriginTypeLabel,
  ORIGIN_TYPE_ORDER,
  RelationshipOrigins,
  RelationshipOriginsTitle,
} from './relationship_origins';
import { getRelationshipTypeLabel, RelationshipSnippet } from './relationship_snippet';
import { RelationshipVisibilitySources } from './relationship_visibility_sources';
import { StatusRevertedTooltip } from '../../company/components/company_onboarding/status_reverted_tooltip';
import { CompanyRelationship, RelationshipCompanyIdentifier } from '../../company/entities';
import { getOnboardingStatusPillKind, getSignupStatusName } from '../../company/utils';
import { FormattedDate } from '../../components/date_formatting/formatted_date';
import { useHasScopes, useIsAdmin } from '../../lib/authorization';
import {
  CompanyRelationshipOriginType,
  CompanyRelationshipType,
  UpdateCompanyRelationshipSignupStatusMutationVariables,
  UnifiedCompanyRelationshipSignupEventType,
  UnifiedCompanyRelationshipSignupStatusUpdateType,
  UnifiedCompanyRelationshipSignupStatus,
} from '../../lib/graphql/result_types';
import { useScrollParent } from '../../lib/table/use_scroll_parent';
import { beautifyString } from '../../lib/util/string';
import { Routes } from '../../routes';
import { DeleteCompanyRelationshipOptions } from '../containers/relationship_mutations_container';
import { VISIBILITY_SOURCES_COLUMN_HINT } from '../visibility_sources';

type ConsentHistoryItem = NonNullable<NonNullable<CompanyRelationship['dataSharingConsent']>['history']>[0];
type SignupEventItem = NonNullable<CompanyRelationship['unifiedSignupEvents']>[0];

// Mirrors the backend `OnboardingActionKey.ONBOARDING_ROLLED_BACK` check, which is exposed on Company but
// not on CompanyRelationship. A relationship is considered rolled back when its current status is no longer
// ONBOARDED yet its history contains an ONBOARDED event.
const getRelationshipRollback = (
  currentStatus: UnifiedCompanyRelationshipSignupStatus | null,
  events: SignupEventItem[] | null,
): { transitionedAt: string | null } | null => {
  if (currentStatus === null || currentStatus === UnifiedCompanyRelationshipSignupStatus.ONBOARDED) {
    return null;
  }
  if (events === null || events.length === 0) {
    return null;
  }
  const sorted = [...events].sort((a, b) => new Date(a.eventTime).getTime() - new Date(b.eventTime).getTime());
  const lastOnboardedIdx = sorted.map((e) => e.type).lastIndexOf(UnifiedCompanyRelationshipSignupEventType.ONBOARDED);
  if (lastOnboardedIdx === -1) {
    return null;
  }
  const eventAfter = sorted[lastOnboardedIdx + 1];
  return { transitionedAt: eventAfter?.eventTime ?? null };
};

const renderTourStatusScope = (value: string): React.ReactNode => {
  switch (value) {
    case 'ACCEPTED':
      return <Pill kind="success">{beautifyString(value)}</Pill>;
    case 'DECLINED':
      return <Pill kind="danger">{beautifyString(value)}</Pill>;
    case 'REQUESTED':
      return <Pill kind="warning">{beautifyString(value)}</Pill>;
    default:
      return <>{value}</>;
  }
};

export interface RelationshipListDataProps {
  data: {
    companyRelationships: CompanyRelationship[];
    company_id: string;
  };
}

interface RelationshipListProps {
  deleteCompanyRelationship: (
    companyRelationshipId: string,
    selectedCompanyId: string,
    options?: DeleteCompanyRelationshipOptions,
  ) => Promise<void>;
  updateCompanyRelationshipSignupStatus: (
    input: UpdateCompanyRelationshipSignupStatusMutationVariables['input'],
    selectedCompanyId: string,
  ) => Promise<void>;
  hasMoreEntries: boolean;
  isLoadingMoreEntries: boolean;
  loadMoreEntries: () => Promise<unknown> | undefined;
}

interface RelationshipRow extends CompanyRelationship {
  id: string;
  visibilitySources: CompanyRelationship['sourceCompany']['signupVisibilitySources'];
}

type ConsentHistoryRow = {
  id: string;
  updatedAt: ConsentHistoryItem['updatedAt'];
  consentSource: ConsentHistoryItem['consentSource'];
  tourStatusScope: ConsentHistoryItem['tourStatusScope'];
  updatedByUser: ConsentHistoryItem['updatedByUser'];
};

const consentHistoryColumns: TableColumns<ConsentHistoryRow> = [
  {
    id: 'updatedAt',
    title: 'Time',
    cellContent: ({ value }) => <FormattedDate date={value} />,
  },
  {
    id: 'consentSource',
    title: 'Consent source',
    cellContent: ({ value }) => beautifyString(value),
  },
  {
    id: 'tourStatusScope',
    title: 'Tour status scope',
    cellContent: ({ value }) => renderTourStatusScope(value),
  },
  {
    id: 'updatedByUser',
    title: 'Triggered by',
    cellContent: ({ value }) =>
      value !== null ? (
        <Link to={Routes.User.generatePath({ user_id: value.user_id })}>
          {value.first_name} {value.last_name}
        </Link>
      ) : (
        '-'
      ),
  },
];

const DataSharingConsentTable: React.FC<{ history: ConsentHistoryItem[] }> = ({ history }) => (
  <CommonUITable data={history.map((item, idx) => ({ id: String(idx), ...item }))} columns={consentHistoryColumns} />
);

type SignupEventHistoryRow = {
  id: string;
  eventTime: SignupEventItem['eventTime'];
  type: SignupEventItem['type'];
  triggeredByUser: SignupEventItem['triggeredByUser'];
};

const signupEventHistoryColumns: TableColumns<SignupEventHistoryRow> = [
  {
    id: 'eventTime',
    title: 'Time',
    cellContent: ({ value }) => <FormattedDate date={value} />,
  },
  {
    id: 'type',
    title: 'Event',
    cellContent: ({ value }) => beautifyString(value),
  },
  {
    id: 'triggeredByUser',
    title: 'Triggered by',
    cellContent: ({ value }) =>
      value !== null ? (
        <Link to={Routes.User.generatePath({ user_id: value.user_id })}>
          {value.first_name} {value.last_name}
        </Link>
      ) : (
        '-'
      ),
  },
];

const SignupEventsHistoryTable: React.FC<{ events: SignupEventItem[] }> = ({ events }) => (
  <CommonUITable
    data={events.map((event, idx) => ({ id: String(idx), ...event }))}
    columns={signupEventHistoryColumns}
  />
);

const getSortValue = (row: RelationshipRow, key: keyof RelationshipRow): string => {
  switch (key) {
    case 'companyRelationshipId':
      return row.companyRelationshipId;
    case 'sourceCompany':
      return row.sourceCompany.company_name ?? '';
    case 'targetCompany':
      return row.targetCompany.company_name ?? '';
    case 'relationshipType':
      return row.relationshipType;
    default:
      return '';
  }
};

const ORIGIN_FILTER_OPTIONS = ORIGIN_TYPE_ORDER.map((type) => ({ value: type, label: getOriginTypeLabel(type) }));

const RELATIONSHIP_TYPE_FILTER_OPTIONS = Object.values(CompanyRelationshipType).map((type) => ({
  value: type,
  label: getRelationshipTypeLabel(type),
}));

const sortData = (data: RelationshipRow[], sortBy: InfiniteTableSortBy<keyof RelationshipRow>): RelationshipRow[] => {
  return [...data].sort((a, b) => {
    const aVal = getSortValue(a, sortBy.columnId);
    const bVal = getSortValue(b, sortBy.columnId);
    const cmp = aVal.localeCompare(bVal);
    return sortBy.order === 'ASC' ? cmp : -cmp;
  });
};

export const RelationshipList: React.FC<RelationshipListProps & RelationshipListDataProps> = ({
  data,
  deleteCompanyRelationship,
  updateCompanyRelationshipSignupStatus,
  hasMoreEntries,
  isLoadingMoreEntries,
  loadMoreEntries,
}) => {
  const { slug_company_name } = useParams<{ slug_company_name?: string }>();
  const { companyRelationships, company_id } = data;

  const [isLoading, setIsLoading] = useState(false);
  const isAdmin = useIsAdmin();
  const hasCompanyRelationshipPermission = useHasScopes([BackofficeScope.companyWriteNetworkRelations]);
  const isPrivilegedUser = isAdmin || hasCompanyRelationshipPermission;

  const [sortBy, setSortBy] = useState<InfiniteTableSortBy<keyof RelationshipRow>>({
    columnId: 'companyRelationshipId',
    order: 'ASC',
  });

  const { scrollParent, containerRefCallback } = useScrollParent();

  const updateRelationshipSignupStatus = useCallback(
    async (input: UpdateCompanyRelationshipSignupStatusMutationVariables['input']) => {
      const companyId = company_id;

      if (isNil(companyId)) {
        throw new Error('No company to update');
      }

      setIsLoading(true);

      try {
        await updateCompanyRelationshipSignupStatus(input, companyId);
      } finally {
        setIsLoading(false);
      }
    },
    [company_id, updateCompanyRelationshipSignupStatus],
  );

  const renderStatusBadge = useCallback((unifiedSignupStatus: UnifiedCompanyRelationshipSignupStatus | null) => {
    if (isNil(unifiedSignupStatus)) {
      return <>-</>;
    }
    return (
      <Pill kind={getOnboardingStatusPillKind(unifiedSignupStatus)}>{getSignupStatusName(unifiedSignupStatus)}</Pill>
    );
  }, []);

  const [originFilter, setOriginFilter] = useState<CompanyRelationshipOriginType[]>([]);
  const [relationshipTypeFilter, setRelationshipTypeFilter] = useState<CompanyRelationshipType[]>([]);
  const [hasAutoLoadFailed, setHasAutoLoadFailed] = useState(false);
  const isFiltered = originFilter.length > 0 || relationshipTypeFilter.length > 0;
  useEffect(() => {
    if (!isFiltered || !hasMoreEntries || isLoadingMoreEntries || hasAutoLoadFailed) {
      return;
    }
    loadMoreEntries()?.catch(() => setHasAutoLoadFailed(true));
  }, [isFiltered, hasMoreEntries, isLoadingMoreEntries, hasAutoLoadFailed, loadMoreEntries]);

  const tableData = useMemo(
    () =>
      sortData(
        (companyRelationships ?? [])
          .filter(({ origins }) => originFilter.length === 0 || origins.some(({ type }) => originFilter.includes(type)))
          .filter(
            ({ relationshipType }) =>
              relationshipTypeFilter.length === 0 || relationshipTypeFilter.includes(relationshipType),
          )
          .map((r) => ({
            ...r,
            id: r.companyRelationshipId,
            visibilitySources: r.sourceCompany.signupVisibilitySources,
          })),
        sortBy,
      ),
    [companyRelationships, sortBy, originFilter, relationshipTypeFilter],
  );

  const columns: InfiniteTableColumns<RelationshipRow> = useMemo(
    () => [
      {
        id: 'companyRelationshipId',
        title: '#',
        width: '5%',
        minWidth: '50px',
        isUserSortable: true,
        align: 'end' as const,
        cellContent: ({ row }) => <>{row.companyRelationshipId}</>,
      },
      {
        id: 'sourceCompany',
        title: 'Source Company',
        width: '14%',
        minWidth: '120px',
        isUserSortable: true,
        cellContent: ({ row }) => (
          <NavLink to={Routes.Company.generatePath({ company_id: row.sourceCompany.company_id })}>
            {row.sourceCompany.company_name}
          </NavLink>
        ),
      },
      {
        id: 'targetCompany',
        title: 'Target Company',
        width: '14%',
        minWidth: '120px',
        isUserSortable: true,
        cellContent: ({ row }) => (
          <NavLink to={Routes.Company.generatePath({ company_id: row.targetCompany.company_id })}>
            {row.targetCompany.company_name}
          </NavLink>
        ),
      },
      {
        id: 'relationshipType',
        title: 'Relationship',
        width: '10%',
        minWidth: '100px',
        isUserSortable: true,
        cellContent: ({ row }) => (
          <RelationshipSnippet
            sourceCompany={row.sourceCompany}
            targetCompany={row.targetCompany}
            relationshipType={row.relationshipType}
          />
        ),
      },
      {
        id: 'origins',
        title: <RelationshipOriginsTitle />,
        width: '10%',
        minWidth: '120px',
        cellContent: ({ row }) => <RelationshipOrigins origins={row.origins} copiedFrom={row.companyGroupCopiedFrom} />,
      },
      {
        id: 'relationIdentifiers',
        title: 'Identifiers',
        width: '10%',
        minWidth: '100px',
        cellContent: ({ row }) => <RelationIdentifierLabels identifiers={row.relationIdentifiers} />,
      },
      {
        id: 'dataSharingConsent',
        title: 'Data sharing consent',
        width: '10%',
        minWidth: '100px',
        cellContent: ({ row }) => (
          <Popover
            maxWidth={550}
            content={
              (row.dataSharingConsent?.history ?? []).length === 0 ? (
                <span>No consent entry exists. This is considered equivalent to a declined consent.</span>
              ) : (
                <DataSharingConsentTable history={row.dataSharingConsent?.history ?? []} />
              )
            }>
            <Stack alignItems="center" gap="xsmall">
              {notNil(row.dataSharingConsent) ? renderTourStatusScope(row.dataSharingConsent.tourStatusScope) : '-'}
              <Icon name="small-info" size={16} />
            </Stack>
          </Popover>
        ),
      },
      {
        id: 'visibilitySources',
        title: (
          <>
            Visibility sources
            <Popover placement="top" content={<Stack direction="column">{VISIBILITY_SOURCES_COLUMN_HINT}</Stack>}>
              <Button iconEnd="small-info-outline" size="xsmall" kind="ghost" hideLabel>
                Help
              </Button>
            </Popover>
          </>
        ),
        width: '12%',
        minWidth: '120px',
        cellContent: ({ row }) => (
          <RelationshipVisibilitySources relationshipType={row.relationshipType} sourceCompany={row.sourceCompany} />
        ),
      },
      {
        id: 'unifiedSignupStatus',
        title: 'Onboarding status',
        width: '10%',
        minWidth: '100px',
        cellContent: ({ row }) => {
          const rollback = getRelationshipRollback(row.unifiedSignupStatus, row.unifiedSignupEvents);
          const statusLabel = notNil(row.unifiedSignupStatus) ? getSignupStatusName(row.unifiedSignupStatus) : '';
          return (
            <Stack direction="row" alignItems="center" gap="small">
              <Popover
                maxWidth={550}
                content={
                  (row.unifiedSignupEvents ?? []).length === 0 ? (
                    <span>No events</span>
                  ) : (
                    <SignupEventsHistoryTable events={row.unifiedSignupEvents ?? []} />
                  )
                }>
                {renderStatusBadge(row.unifiedSignupStatus)}
              </Popover>
              {rollback !== null && (
                <StatusRevertedTooltip statusLabel={statusLabel} transitionedAt={rollback.transitionedAt} />
              )}
            </Stack>
          );
        },
      },
      {
        id: 'unifiedSignupEvents',
        title: 'Update onboarding status',
        width: '12%',
        minWidth: '130px',
        cellContent: ({ row }) => (
          <CommonDropdown align="end">
            <DropdownButton size="small" disabled={isLoading} iconEnd="small-chevron-down">
              <Text>Update</Text>
            </DropdownButton>
            <DropdownMenu>
              <DropdownMenuItem
                onClick={async () =>
                  await updateRelationshipSignupStatus({
                    companyRelationshipId: row.companyRelationshipId,
                    update: UnifiedCompanyRelationshipSignupStatusUpdateType.RESET_TO_AUTOMATIC,
                  })
                }>
                Reset to calculated status
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={async () =>
                  await updateRelationshipSignupStatus({
                    companyRelationshipId: row.companyRelationshipId,
                    update: UnifiedCompanyRelationshipSignupStatusUpdateType.OUT_OF_SCOPE,
                  })
                }>
                Out of scope
              </DropdownMenuItem>
            </DropdownMenu>
          </CommonDropdown>
        ),
      },
      {
        id: 'id',
        title: 'Actions',
        width: '8%',
        minWidth: '80px',
        cellContent: ({ row }: { row: RelationshipRow }) => (
          <DeleteRelationshipAction
            identifiers={row.relationIdentifiers}
            carrier={{ companyId: row.sourceCompany.company_id, companyName: row.sourceCompany.company_name }}
            targetCompanyId={row.targetCompany.company_id}
            relationshipType={row.relationshipType}
            disabled={!isPrivilegedUser}
            onDelete={(options) => deleteCompanyRelationship(row.companyRelationshipId, company_id, options)}
          />
        ),
      },
    ],
    [
      isLoading,
      isPrivilegedUser,
      company_id,
      deleteCompanyRelationship,
      updateRelationshipSignupStatus,
      renderStatusBadge,
    ],
  );

  return (
    <Stack direction="column" gap="medium">
      <Stack justifyContent="start" alignItems="center">
        <h2 style={{ margin: 0 }}>Company Relationships</h2>
        {isPrivilegedUser && (
          <NavLink to={Routes.CompanyRelationshipCreate.generatePath({ company_id, slug_company_name })}>
            <Button kind="primary" size="small">
              Add Relationship
            </Button>
          </NavLink>
        )}
        <div style={{ width: 320 }}>
          <MultiSelect
            label="Relationship type"
            labelPosition="hidden"
            placeholder="All relationship types"
            clearAllLabel="Clear all"
            {...{ closeMenuOnSelect: false }}
            options={RELATIONSHIP_TYPE_FILTER_OPTIONS}
            value={RELATIONSHIP_TYPE_FILTER_OPTIONS.filter(({ value }) => relationshipTypeFilter.includes(value))}
            onChange={(selected) =>
              setRelationshipTypeFilter(
                Array.isArray(selected) ? selected.map(({ value }) => value as CompanyRelationshipType) : [],
              )
            }
          />
        </div>
        <div style={{ width: 320 }}>
          <MultiSelect
            label="Origin"
            labelPosition="hidden"
            placeholder="All origins"
            clearAllLabel="Clear all"
            {...{ closeMenuOnSelect: false }}
            options={ORIGIN_FILTER_OPTIONS}
            value={ORIGIN_FILTER_OPTIONS.filter(({ value }) => originFilter.includes(value))}
            onChange={(selected) =>
              setOriginFilter(
                Array.isArray(selected) ? selected.map(({ value }) => value as CompanyRelationshipOriginType) : [],
              )
            }
          />
        </div>
        <Text prominence="subtle">
          {tableData.length} of {companyRelationships?.length ?? 0}
          {hasMoreEntries ? '+' : ''} relationships
        </Text>
      </Stack>

      <div className="sub-table-scroll-container" style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        <div ref={containerRefCallback}>
          {scrollParent !== null && (
            <InfiniteTable<RelationshipRow>
              columns={columns}
              data={tableData}
              sortBy={sortBy}
              onSortByChange={setSortBy}
              scrollParent={scrollParent}
              className="compact-table"
            />
          )}
        </div>
      </div>
    </Stack>
  );
};

const RelationIdentifierLabels = ({ identifiers }: { identifiers: RelationshipCompanyIdentifier[] }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const hasMoreThan2 = identifiers.length > 2;
  const visibleIdentifiers = isExpanded ? identifiers : identifiers.slice(0, 2);

  if (identifiers.length === 0) {
    return <>-</>;
  }

  return (
    <>
      {visibleIdentifiers.map(({ identifierValue, companyIdentifierId, source }) => (
        <Box
          padding="xsmall"
          radius="small"
          className="company__relationships-identifierContainer"
          key={companyIdentifierId}>
          <Stack justifyContent="space-between" alignItems="center">
            <Text weight="bold" size="small">
              {identifierValue}
            </Text>
            <Text style={{ fontSize: '7px' }}>{source}</Text>
          </Stack>
        </Box>
      ))}
      {hasMoreThan2 && (
        <CommonLink as="button" onClick={() => setIsExpanded(!isExpanded)}>
          {isExpanded ? 'Show less' : 'Show more'}
        </CommonLink>
      )}
    </>
  );
};
